import React, { useContext, useEffect, useRef, useState } from "react";
import { Modal, Form, Row, Col, InputGroup, ToggleButtonGroup, ToggleButton } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Formik, FieldArray, useFormikContext } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import { FlitServiceContext } from "../../services/flitService";
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { InvitationDialog } from "../BusinessEntity/InvitationDialog";
import { 
  FREIGHT_MODE, 
  INCOTERMS, 
  MEASUREMENT_SYSTEM_TYPE, 
  PACKAGE_TYPE,
  PACKAGE_MEASUREMENT_TYPE,
  CONSOL_STATUS,
  QUOTE_REQUEST_STATUS,
  QUOTE_REQUEST_TYPE,
  STAKEHOLDER_TYPE,
  ROLES
} from "../../services/flitEnum";
import { LinearProgress, IconButton, Button } from "@material-ui/core";
import DeleteIcon from '@material-ui/icons/Delete';
import { useLang } from "../../../_metronic/i18n/Metronici18n";
import moment from "moment";
import cf from '../../services/codeformatter';


const PER_PAGE = 5;
const activeColor = { background: "#f1711c", borderColor: "#f1711c" };
const fixFormRow = { marginRight: "0px", marginLeft: "0px" };

export function NewConsolOrderDialog({ show, onHide, consolSchedule, orderId }) {
  const intl = useIntl();
  const lang = useLang();
  const { flitSvc } = useContext(FlitServiceContext);
  const { user } = useSelector(state => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const isEditMode = !!orderId;
  const [existingOrderData, setExistingOrderData] = useState(null);

  // Invite User States
  const [showInviteUser, setShowInviteUser] = useState(false);

  // Business entity search states
  const [businessEntityOptions, setBusinessEntityOptions] = useState([]);
  const [businessEntitySearchCount, setBusinessEntitySearchCount] = useState(0);
  const [businessEntitySearchString, setBusinessEntitySearchString] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedBusinessEntity, setSelectedBusinessEntity] = useState(null);

  // Freight owner states
  const [businessEntityMembers, setBusinessEntityMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const setFieldValueRef = useRef(null);
  
  const initialValues = {
    businessEntityId: "",
    submittedById: "",
    status: CONSOL_STATUS.DRAFT,
    description: "",
    incoterms: "",
    totalWeight: "",
    totalVolume: "",
    measurementSystem: MEASUREMENT_SYSTEM_TYPE.METRIC,
    packages: [
      {
        packageType: PACKAGE_TYPE.BOX,
        numberOfPackages: 1,
        packageMeasurementType: PACKAGE_MEASUREMENT_TYPE.DIMENSION,
        length: '',
        width: '',
        height: '',
        volume: '',
        weight: ''
      }
    ],
    isPickup: false,
    originDropOffOn: "",
    houseBillNo: "",
    note: ""
  };

  const [formValues, setFormValues] = useState(initialValues);

  const NewConsolOrderSchema = Yup.object().shape({
    businessEntityId: isEditMode 
      ? Yup.string().nullable()
      : Yup.string().required(
          intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
        ),
    submittedById: isEditMode
      ? Yup.string().nullable()
      : Yup.string().required(
          intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
        ),
    status: Yup.string().required(
      intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })
    ),
    incoterms: Yup.string().required(
      intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })
    ),
    totalWeight: Yup.number().nullable().notRequired(),
    totalVolume: Yup.number().nullable().notRequired()
  });

  useEffect(() => {
    if (orderId && show) {
      setIsLoading(true);
      flitSvc.getConsol(orderId).then(({ data }) => {
        setExistingOrderData(data);
        setIsLoading(false);
      }).catch(() => {
        setIsLoading(false);
      });
    } else {
      setExistingOrderData(null);
      setFormValues(initialValues); 
      setSelectedBusinessEntity(null); 
    }
  }, [orderId, show]);

  useEffect(() => {
    if (selectedBusinessEntity) {
      loadMembers(selectedBusinessEntity.id);
    } else {
      setBusinessEntityMembers([]);
    }
  }, [selectedBusinessEntity]);

  useEffect(() => {
    if (existingOrderData) {
      const quoteRequest = existingOrderData.activity?.quoteRequest?.[0];
      const updatedFormValues = {
        ...initialValues,
        businessEntityId: "",
        submittedById: quoteRequest?.submittedById || "",
        status: existingOrderData.status || CONSOL_STATUS.DRAFT, 
        description: existingOrderData.description || "",
        incoterms: existingOrderData.incoterms || "",
        totalWeight: existingOrderData.totalWeight || "",
        totalVolume: existingOrderData.totalVolume || "",
        measurementSystem: quoteRequest?.measurementSystem || MEASUREMENT_SYSTEM_TYPE.METRIC,
        packages: quoteRequest?.packages?.length > 0 ? quoteRequest.packages : initialValues.packages,
        isPickup: quoteRequest?.isPickup || false,
        originDropOffOn: existingOrderData.originDropOffOn ? new Date(existingOrderData.originDropOffOn) : "",
        houseBillNo: existingOrderData.houseBillNo || "",
        note: existingOrderData.note || ""
      };
      setFormValues(updatedFormValues);

      if (existingOrderData.businessEntityId) {
        flitSvc.getBusinessEntity(existingOrderData.businessEntityId).then(({ data }) => {
          setSelectedBusinessEntity(data);
        }).catch((error) => {
          console.error("Error fetching business entity:", error);
        });
      }
    }
  }, [existingOrderData]);

  // Updated loadMembers to optionally select a specific user after loading
  function loadMembers(businessEntityId, autoSelectUserId = null) {
    setLoadingMembers(true);
    flitSvc.getUsersForBusinessEntity(businessEntityId)
      .then(({ data }) => {
        setBusinessEntityMembers(data);
        
        if (setFieldValueRef.current) {
          if (autoSelectUserId) {
             // If we just created a user, select them
             const newUser = data.find(u => u.id === autoSelectUserId);
             if (newUser) {
               setFieldValueRef.current('submittedById', newUser.id);
             }
          } else if (data.length === 1) {
            // Default behavior if only one user exists
            setFieldValueRef.current('submittedById', data[0].id);
          }
        }
      })
      .catch((error) => {
        console.error("Error loading freight owners:", error);
        setBusinessEntityMembers([]);
      })
      .finally(() => {
        setLoadingMembers(false);
      });
  }

  // Callback when a user is successfully invited/created
  const handleInviteSuccess = (invitedUserId) => {
    setShowInviteUser(false);
    if (selectedBusinessEntity) {
      loadMembers(selectedBusinessEntity.id, invitedUserId); 
    }
  };

  function _handleBusinessEntitySearch(name) {
    setBusinessEntitySearchString(name);
    setSearchLoading(true);
    flitSvc.searchBusinessEntityIdByName(name, PER_PAGE, 0)
      .then(({ data }) => {
        setBusinessEntityOptions(data.value);
        setBusinessEntitySearchCount(data['@odata.count'] || 0);
      })
      .finally(() => {
        setSearchLoading(false);
      });
  }

  function _filterBusinessEntitySearchCallback(option, props) {
    return true;
  }

  function _handleBusinessEntitySearchPagination(e, shownResults) {
    if (
      businessEntityOptions.length >= shownResults ||
      businessEntityOptions.length >= businessEntitySearchCount
    ) {
      return;
    }
    setSearchLoading(true);
    flitSvc.searchBusinessEntityIdByName(
      businessEntitySearchString, 
      shownResults - businessEntityOptions.length + 1, 
      businessEntityOptions.length
    )
      .then(({ data }) => {
        setBusinessEntityOptions(businessEntityOptions.concat(data.value));
        setBusinessEntitySearchCount(data['@odata.count'] || 0);
      })
      .finally(() => {
        setSearchLoading(false);
      });
  }

  function convertWeight(sourceSystem, value) {
    if (value) {
      if (sourceSystem === MEASUREMENT_SYSTEM_TYPE.METRIC) {
        return Math.round(value * 2.205 * 100) / 100;
      } else {
        return Math.round(value / 2.205 * 100) / 100;
      }
    }
  }

  function convertVolume(sourceSystem, value) {
    if (value) {
      if (sourceSystem === MEASUREMENT_SYSTEM_TYPE.METRIC) {
        return Math.round(value * 35.315 * 1000) / 1000;
      } else {
        return Math.round(value / 35.315 * 1000) / 1000;
      }
    }
  }

  function convertLength(sourceSystem, value) {
    if (value) {
      if (sourceSystem === MEASUREMENT_SYSTEM_TYPE.METRIC) {
        return Math.round(value * 0.393701 * 100) / 100;
      } else {
        return Math.round(value / 0.393701 * 100) / 100;
      }
    }
  }

  function updateTotalWeight(newWeight, updatedIndex, values) {
    var totalWeight = newWeight;
    values.packages.forEach((aPackage, index) => {
      if (index !== updatedIndex) {
        if (aPackage.numberOfPackages && aPackage.weight) {
          totalWeight += aPackage.numberOfPackages * aPackage.weight;
        }
      }
    });
    return Math.round(totalWeight * 100) / 100;
  }

  function updateTotalVolume(updatedData, updatedIndex, values) {
    var totalVolume = updatedData;
    values.packages.forEach((aPackage, index) => {
      if (index !== updatedIndex) {
        if (aPackage.numberOfPackages) {
          if (aPackage.packageMeasurementType === PACKAGE_MEASUREMENT_TYPE.DIMENSION && 
              aPackage.width && aPackage.length && aPackage.height) {
            totalVolume += aPackage.numberOfPackages * aPackage.width * aPackage.length * aPackage.height / 
              (values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? 1000000 : 1728);
          } else if (aPackage.packageMeasurementType === PACKAGE_MEASUREMENT_TYPE.VOLUME && aPackage.volume) {
            totalVolume += aPackage.numberOfPackages * aPackage.volume;
          }
        }
      }
    });
    return Math.round(totalVolume * 100) / 100;
  }

  function recalculateTotals(packages, measurementSystem, setFieldValue) {
    const totalWeight = updateTotalWeight(0, -1, { packages, measurementSystem });
    const totalVolume = updateTotalVolume(0, -1, { packages, measurementSystem });
    setFieldValue('totalWeight', totalWeight);
    setFieldValue('totalVolume', totalVolume);
  }

  return (
    <>
      <Modal
        size="lg"
        show={show}
        onHide={() => onHide(false)}
        aria-labelledby="new-consol-order-dialog"
        centered={true}
      >
        <Formik
          enableReinitialize={true}
          validationSchema={NewConsolOrderSchema}
          initialValues={formValues}
          validateOnChange={true}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            
            if (isEditMode) {
              const quoteRequestId = existingOrderData.activity?.quoteRequest?.[0]?.id;
              if (quoteRequestId) {
                flitSvc.updateQuoteRequestConsolData(
                  quoteRequestId,
                  values.packages,
                  values.measurementSystem,
                  values.totalWeight,
                  values.totalVolume
                ).then(() => {
                  return flitSvc.updateConsolDataInMetric(
                    orderId,
                    values.status,
                    values.incoterms,
                    values.houseBillNo,
                    values.totalWeight,
                    values.totalVolume,
                    values.packages[0]?.packageType || PACKAGE_TYPE.BOX,
                    values.packages.reduce((sum, pkg) => sum + (pkg.numberOfPackages || 0), 0),
                    values.measurementSystem,
                    values.isPickup, 
                    values.originDropOffOn,
                    values.note
                  );
                }).then(() => {
                  setSubmitting(false);
                  onHide(true);
                }).catch((error) => {
                  console.error("Error updating order:", error);
                  setSubmitting(false);
                });
              } else {
                setSubmitting(false);
              }
            } else {
              const quoteRequest = {
                type: QUOTE_REQUEST_TYPE.CONSOL,
                consolScheduleId: consolSchedule.id,
                status: QUOTE_REQUEST_STATUS.SUBMITTED,
                description: values.description || `Consol order on ${moment().format("l")}`,
                mode: consolSchedule?.mode || FREIGHT_MODE.OCEAN,
                freightType: "LCL",
                incoterms: values.incoterms,
                originLocationId: consolSchedule.originLocationId,
                destinationLocationId: consolSchedule.destinationLocationId,
                packages: values.packages,
                totalWeight: values.totalWeight,
                totalVolume: values.totalVolume,
                measurementSystem: values.measurementSystem,
                isPickup: values.isPickup,
                submittedById: values.submittedById,
                activity: {
                  stakeholder: [
                    {
                      businessEntityId: values.businessEntityId,
                      type: STAKEHOLDER_TYPE.OWNER
                    }
                  ]
                }
              };

              flitSvc.createQuoteRequest(quoteRequest).then(({ data }) => {
                return flitSvc.getConsolByActivityId(data.activityId);
              }).then(({ data }) => {
                const totalPackages = values.packages.reduce((sum, pkg) => sum + (pkg.numberOfPackages || 0), 0);
                return flitSvc.updateConsolDataInMetric(
                  data.id,
                  values.status,
                  values.incoterms,
                  values.houseBillNo,
                  values.totalWeight,
                  values.totalVolume,
                  values.packages[0]?.packageType || PACKAGE_TYPE.BOX,
                  totalPackages,
                  values.measurementSystem,
                  values.isPickup,
                  values.originDropOffOn,
                  values.note
                )
              }).then(() => {
                onHide(true);
              }).catch((error) => {
                console.error("Error creating consol order:", error);
              }).finally(() => {
                setSubmitting(false);
              });
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting
          }) => {
            setFieldValueRef.current = setFieldValue;
            return (
              <Form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Modal.Header closeButton>
                  <Modal.Title id="new-consol-order-dialog">
                    {intl.formatMessage({ id: isEditMode ? "EDIT_CONSOL_ORDER" : "NEW_CONSOL_ORDER" })}
                  </Modal.Title>
                </Modal.Header>
                
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {isLoading && <LinearProgress />}
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="status">
                        <Form.Label>
                          {intl.formatMessage({ id: "STATUS" })} *
                        </Form.Label>
                        <Form.Control
                          as="select"
                          name="status"
                          value={values.status}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={touched.status && errors.status ? "is-invalid" : ""}
                        >
                          <option value={CONSOL_STATUS.DRAFT}>
                            {cf.format('ConsolStatus', CONSOL_STATUS.DRAFT, intl.locale)}
                          </option>
                          <option value={CONSOL_STATUS.SUBMITTED}>
                            {cf.format('ConsolStatus', CONSOL_STATUS.SUBMITTED, intl.locale)}
                          </option>
                          <option value={CONSOL_STATUS.CONFIRMED}>
                            {cf.format('ConsolStatus', CONSOL_STATUS.CONFIRMED, intl.locale)}
                          </option>
                          <option value={CONSOL_STATUS.RECEIVED}>
                            {cf.format('ConsolStatus', CONSOL_STATUS.RECEIVED, intl.locale)}
                          </option>
                          <option value={CONSOL_STATUS.CANCELLED}>
                            {cf.format('ConsolStatus', CONSOL_STATUS.CANCELLED, intl.locale)}
                          </option>
                        </Form.Control>
                        {touched.status && errors.status && (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">{errors.status}</div>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    {!isEditMode && (
                      <Col md={6}>
                        <Form.Group controlId="businessEntityId">
                          <Form.Label>
                            {intl.formatMessage({ id: "BUSINESS_ENTITY" })} *
                          </Form.Label>
                          <AsyncTypeahead
                            id="businessEntitySearch"
                            onChange={(selected) => {
                              if (selected.length > 0) {
                                setSelectedBusinessEntity(selected[0]);
                                setFieldValue('businessEntityId', selected[0].id);
                                setFieldValue('submittedById', ''); 
                              } else {
                                setSelectedBusinessEntity(null);
                                setFieldValue('businessEntityId', '');
                                setFieldValue('submittedById', '');
                              }
                            }}
                            onInputChange={(text, event) => {
                              if (!text) _handleBusinessEntitySearch('');
                            }}
                            selected={selectedBusinessEntity ? [selectedBusinessEntity] : []}
                            paginate
                            maxResults={PER_PAGE - 1}
                            isLoading={searchLoading}
                            multiple={false}
                            allowNew={false}
                            options={businessEntityOptions}
                            labelKey={(option) => `${option.legalName}`}
                            minLength={0}
                            onSearch={_handleBusinessEntitySearch}
                            filterBy={_filterBusinessEntitySearchCallback}
                            onPaginate={_handleBusinessEntitySearchPagination}
                            placeholder={intl.formatMessage({ id: "SEARCH_BUSINESS_ENTITY" })}
                            renderMenuItemChildren={(option, props) => (
                              <div key={option.id}>
                                <strong>{`${option.legalName}${option.localizedLegalName ? ` (${option.localizedLegalName})` : ''}`}</strong>
                              </div>
                            )}
                            paginationText={intl.formatMessage({ id: "SHOW_MORE_RESULTS" })}
                            useCache={false}
                          />
                          {touched.businessEntityId && errors.businessEntityId && (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">{errors.businessEntityId}</div>
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    )}
                    
                    <Col md={6}>
                      <Form.Group controlId="submittedById">
                        <Form.Label>
                          {intl.formatMessage({ id: "SUBMITTED_BY" })} *
                        </Form.Label>
                        {isEditMode ? (
                          <Form.Control
                            type="text"
                            value={existingOrderData?.activity?.quoteRequest[0]?.submittedBy
                              ? `${existingOrderData?.activity?.quoteRequest[0].submittedBy.firstName} ${existingOrderData?.activity?.quoteRequest[0].submittedBy.lastName} (${existingOrderData?.activity?.quoteRequest[0].submittedBy.email})`
                              : ''}
                            readOnly
                          />
                        ) : (
                          <>
                            <Form.Control
                              as="select"
                              name="submittedById"
                              value={values.submittedById}
                              onChange={(e) => {
                                // NEW LOGIC: Check if "NEW_USER" is selected
                                if (e.target.value === "NEW_USER") {
                                  setShowInviteUser(true);
                                  // Do not update Formik value here; wait for success
                                } else {
                                  handleChange(e);
                                }
                              }}
                              onBlur={handleBlur}
                              disabled={!selectedBusinessEntity || loadingMembers}
                              className={touched.submittedById && errors.submittedById ? "is-invalid" : ""}
                            >
                              <option value="">{intl.formatMessage({ id: "SELECT_SUBMITTED_BY" })}</option>
                              
                              {/* NEW OPTION FOR INVITE */}
                              <option value="NEW_USER" className="font-weight-bold text-primary">
                                + {intl.formatMessage({ id: "ADD_USER" })}...
                              </option>

                              {businessEntityMembers?.map(user => (
                                <option key={user.id} value={user.id}>
                                  {`${user.firstName} ${user.lastName} (${user.email})`}
                                </option>
                              ))}
                            </Form.Control>
                            {loadingMembers && (
                              <small className="text-muted">
                                {intl.formatMessage({ id: "LOADING" })}...
                              </small>
                            )}
                            {!selectedBusinessEntity && (
                              <small className="text-muted">
                                {intl.formatMessage({ id: "SELECT_BUSINESS_ENTITY_FIRST" })}
                              </small>
                            )}
                          </>
                        )}
                        {touched.submittedById && errors.submittedById && (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">{errors.submittedById}</div>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="incoterms">
                        <Form.Label>
                          {intl.formatMessage({ id: "INCOTERMS" })} *
                        </Form.Label>
                        <Form.Control
                          as="select"
                          name="incoterms"
                          value={values.incoterms}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={touched.incoterms && errors.incoterms ? "is-invalid" : ""}
                        >
                          <option value="">{intl.formatMessage({ id: "SELECT_INCOTERMS" })}</option>
                          <option value={INCOTERMS.DDP}>DDP</option>
                          <option value={INCOTERMS.FOB}>FOB</option>
                          <option value={INCOTERMS.CIF}>CIF</option>
                          <option value={INCOTERMS.CFR}>CFR</option>
                          <option value={INCOTERMS.CIP}>CIP</option>
                          <option value={INCOTERMS.CPT}>CPT</option>
                          <option value={INCOTERMS.DAP}>DAP</option>
                          <option value={INCOTERMS.DAT}>DAT</option>
                          <option value={INCOTERMS.DPU}>DPU</option>
                          <option value={INCOTERMS.EXW}>EXW</option>
                          <option value={INCOTERMS.FAS}>FAS</option>
                          <option value={INCOTERMS.FCA}>FCA</option>
                        </Form.Control>
                        {touched.incoterms && errors.incoterms && (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">{errors.incoterms}</div>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group controlId="houseBillNo">
                        <Form.Label>{intl.formatMessage({ id: "HBL" })}</Form.Label>
                        <Form.Control
                          type="text"
                          name="houseBillNo"
                          value={values.houseBillNo}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={intl.formatMessage({ id: "HOUSE_BILL_NO" })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />
                  {/* Measurement System Toggle */}
                  <Row>
                    <Col>
                      <Form.Label><b>{intl.formatMessage({ id: "CARGO_INFORMATION" })}</b></Form.Label>
                      <ToggleButtonGroup
                        as={Row}
                        id="measurementSystem"
                        type="radio"
                        name="measurementSystem"
                        size="sm"
                        defaultValue={MEASUREMENT_SYSTEM_TYPE.METRIC}
                        className="float-right"
                      >
                        <ToggleButton
                          variant="secondary"
                          value={MEASUREMENT_SYSTEM_TYPE.METRIC}
                          style={values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? activeColor : {}}
                          onChange={(e) => {
                            setFieldValue('totalWeight', convertWeight(values.measurementSystem, values.totalWeight));
                            setFieldValue('totalVolume', convertVolume(values.measurementSystem, values.totalVolume));
                            values.packages.forEach((aPackage, index) => {
                              setFieldValue(`packages.${index}.length`, convertLength(values.measurementSystem, aPackage.length));
                              setFieldValue(`packages.${index}.width`, convertLength(values.measurementSystem, aPackage.width));
                              setFieldValue(`packages.${index}.height`, convertLength(values.measurementSystem, aPackage.height));
                              setFieldValue(`packages.${index}.volume`, convertVolume(values.measurementSystem, aPackage.volume));
                              setFieldValue(`packages.${index}.weight`, convertWeight(values.measurementSystem, aPackage.weight));
                            });
                            handleChange(e);
                          }}
                        >
                          {intl.formatMessage({ id: "METRIC" })}
                        </ToggleButton>
                        <ToggleButton
                          variant="secondary"
                          value={MEASUREMENT_SYSTEM_TYPE.IMPERIAL}
                          style={values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.IMPERIAL ? activeColor : {}}
                          onChange={(e) => {
                            setFieldValue('totalWeight', convertWeight(values.measurementSystem, values.totalWeight));
                            setFieldValue('totalVolume', convertVolume(values.measurementSystem, values.totalVolume));
                            values.packages.forEach((aPackage, index) => {
                              setFieldValue(`packages.${index}.length`, convertLength(values.measurementSystem, aPackage.length));
                              setFieldValue(`packages.${index}.width`, convertLength(values.measurementSystem, aPackage.width));
                              setFieldValue(`packages.${index}.height`, convertLength(values.measurementSystem, aPackage.height));
                              setFieldValue(`packages.${index}.volume`, convertVolume(values.measurementSystem, aPackage.volume));
                              setFieldValue(`packages.${index}.weight`, convertWeight(values.measurementSystem, aPackage.weight));
                            });
                            handleChange(e);
                          }}
                        >
                          {intl.formatMessage({ id: "IMPERIAL" })}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Col>
                  </Row>

                  <br />

                  {/* Package Details */}
                  <FieldArray
                    name="packages"
                    render={arrayHelpers => (
                      <div>
                        {values.packages.map((aPackage, index) => (
                          <React.Fragment key={index}>
                            <Row>
                              <Col md="3">
                                <Form.Group controlId={`packages.${index}.packageType`} className="mb-3">
                                  <Form.Label>{intl.formatMessage({ id: "PACKAGE_TYPE" })}</Form.Label>
                                  <Form.Control
                                    as="select"
                                    size="sm"
                                    name={`packages.${index}.packageType`}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={aPackage.packageType}
                                  >
                                    <option value=''>{intl.formatMessage({ id: "SELECT_TYPE" })}</option>
                                    <option value={PACKAGE_TYPE.PALLET}>{intl.formatMessage({ id: "PALLET" })}</option>
                                    <option value={PACKAGE_TYPE.CRATE}>{intl.formatMessage({ id: "CRATE" })}</option>
                                    <option value={PACKAGE_TYPE.BOX}>{intl.formatMessage({ id: "BOX" })}</option>
                                    <option value={PACKAGE_TYPE.OTHER}>{intl.formatMessage({ id: "OTHER" })}</option>
                                  </Form.Control>
                                </Form.Group>
                              </Col>
                              
                              <Col md="3">
                                <Form.Group controlId={`packages.${index}.numberOfPackages`} className="mb-3">
                                  <Form.Label>{intl.formatMessage({ id: "NUMBER_OF_UNITS" })}</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    name={`packages.${index}.numberOfPackages`}
                                    type="number"
                                    onChange={(e) => {
                                      const numPackages = e.target.value ? parseFloat(e.target.value) : 0;
                                      
                                      let newVolume = 0;
                                      if (aPackage.packageMeasurementType === PACKAGE_MEASUREMENT_TYPE.DIMENSION && 
                                          aPackage.width && aPackage.length && aPackage.height) {
                                        const perUnitVolume = aPackage.width * aPackage.length * aPackage.height / 
                                          (values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? 1000000 : 1728);
                                        newVolume = numPackages * perUnitVolume;
                                        setFieldValue(`packages.${index}.volume`, Math.round(perUnitVolume * 1000) / 1000);
                                      } else if (aPackage.packageMeasurementType === PACKAGE_MEASUREMENT_TYPE.VOLUME && aPackage.volume) {
                                        newVolume = numPackages * aPackage.volume;
                                      }
                                      setFieldValue('totalVolume', updateTotalVolume(newVolume, index, values));
                                      
                                      if (aPackage.weight) {
                                        setFieldValue('totalWeight', updateTotalWeight(numPackages * aPackage.weight, index, values));
                                      }
                                      
                                      handleChange(e);
                                    }}
                                    onBlur={handleBlur}
                                    value={aPackage.numberOfPackages}
                                  />
                                </Form.Group>
                              </Col>
                              
                              <Col md="4" className="mb-3">
                                <Row style={fixFormRow}>
                                  <Form.Label>*{intl.formatMessage({ id: "PER_UNIT" })}</Form.Label>
                                </Row>
                                <ToggleButtonGroup
                                  as={Row}
                                  id={`packages.${index}.packageMeasurementType`}
                                  type="radio"
                                  style={fixFormRow}
                                  name={`packages.${index}.packageMeasurementType`}
                                  size="sm"
                                  defaultValue={PACKAGE_MEASUREMENT_TYPE.DIMENSION}
                                >
                                  <ToggleButton
                                    variant="secondary"
                                    value={PACKAGE_MEASUREMENT_TYPE.DIMENSION}
                                    style={aPackage.packageMeasurementType === PACKAGE_MEASUREMENT_TYPE.DIMENSION ? activeColor : {}}
                                    onChange={(e) => {
                                      if (aPackage.numberOfPackages && aPackage.width && aPackage.length && aPackage.height) {
                                        const perUnitVolume = aPackage.length * aPackage.width * aPackage.height / 
                                          (values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? 1000000 : 1728);
                                        const totalVolume = perUnitVolume * aPackage.numberOfPackages;
                                        setFieldValue(`packages.${index}.volume`, Math.round(perUnitVolume * 1000) / 1000);
                                        setFieldValue('totalVolume', updateTotalVolume(totalVolume, index, values));
                                      } else {
                                        setFieldValue(`packages.${index}.volume`, '');
                                        setFieldValue('totalVolume', updateTotalVolume(0, index, values));
                                      }
                                      handleChange(e);
                                    }}
                                  >
                                    {intl.formatMessage({ id: "DIMENSION" })}
                                  </ToggleButton>
                                  <ToggleButton
                                    variant="secondary"
                                    value={PACKAGE_MEASUREMENT_TYPE.VOLUME}
                                    style={aPackage.packageMeasurementType === PACKAGE_MEASUREMENT_TYPE.VOLUME ? activeColor : {}}
                                    onChange={(e) => {
                                      if (aPackage.numberOfPackages && aPackage.volume) {
                                        setFieldValue('totalVolume', updateTotalVolume(aPackage.numberOfPackages * aPackage.volume, index, values));
                                      } else {
                                        setFieldValue('totalVolume', updateTotalVolume(0, index, values));
                                      }
                                      handleChange(e);
                                    }}
                                  >
                                    {intl.formatMessage({ id: "VOLUME" })}
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              </Col>
                              
                              {values.packages.length > 1 && (
                                <Col>
                                  <IconButton
                                    aria-label="Delete"
                                    onClick={() => {
                                      arrayHelpers.remove(index);
                                      setTimeout(() => {
                                        recalculateTotals(values.packages.filter((_, i) => i !== index), values.measurementSystem, setFieldValue);
                                      }, 0);
                                    }}
                                    color="secondary"
                                    style={{ marginTop: "15px" }}
                                  >
                                    <DeleteIcon fontSize="inherit" />
                                  </IconButton>
                                </Col>
                              )}
                            </Row>

                            {aPackage.packageMeasurementType === PACKAGE_MEASUREMENT_TYPE.VOLUME && (
                              <Row>
                                <Col md="4">
                                  <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Prepend>
                                      <InputGroup.Text>{intl.formatMessage({ id: "VOLUME" })}</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                      name={`packages.${index}.volume`}
                                      type="number"
                                      onChange={(e) => {
                                        if (aPackage.numberOfPackages) {
                                          setFieldValue('totalVolume', updateTotalVolume(
                                            (e.target.value ? e.target.value : 0) * aPackage.numberOfPackages,
                                            index,
                                            values
                                          ));
                                        }
                                        handleChange(e);
                                      }}
                                      onBlur={handleBlur}
                                      value={aPackage.volume}
                                    />
                                    <InputGroup.Append>
                                      <InputGroup.Text>
                                        {values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "cbm" : "cbf"}
                                      </InputGroup.Text>
                                    </InputGroup.Append>
                                  </InputGroup>
                                </Col>
                                <Col md="4">
                                  <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Prepend>
                                      <InputGroup.Text>{intl.formatMessage({ id: "WEIGHT" })}</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                      name={`packages.${index}.weight`}
                                      type="number"
                                      onChange={(e) => {
                                        if (aPackage.numberOfPackages) {
                                          setFieldValue('totalWeight', updateTotalWeight(
                                            (e.target.value ? e.target.value : 0) * aPackage.numberOfPackages,
                                            index,
                                            values
                                          ));
                                        }
                                        handleChange(e);
                                      }}
                                      onBlur={handleBlur}
                                      value={aPackage.weight}
                                    />
                                    <InputGroup.Append>
                                      <InputGroup.Text>
                                        {values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "kg" : "lb"}
                                      </InputGroup.Text>
                                    </InputGroup.Append>
                                  </InputGroup>
                                </Col>
                              </Row>
                            )}

                            {aPackage.packageMeasurementType === PACKAGE_MEASUREMENT_TYPE.DIMENSION && (
                              <Row>
                                <Col md="3">
                                  <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Prepend>
                                      <InputGroup.Text>L</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                      name={`packages.${index}.length`}
                                      type="number"
                                      onChange={(e) => {
                                        const lengthValue = e.target.value ? parseFloat(e.target.value) : 0;
                                        if (aPackage.width && aPackage.height && aPackage.numberOfPackages) {
                                          const perUnitVolume = lengthValue * aPackage.width * aPackage.height / 
                                            (values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? 1000000 : 1728);
                                          const totalVolume = perUnitVolume * aPackage.numberOfPackages;
                                          setFieldValue(`packages.${index}.volume`, Math.round(perUnitVolume * 1000) / 1000);
                                          setFieldValue('totalVolume', updateTotalVolume(totalVolume, index, values));
                                        }
                                        handleChange(e);
                                      }}
                                      onBlur={handleBlur}
                                      value={aPackage.length}
                                    />
                                    <InputGroup.Append>
                                      <InputGroup.Text>
                                        {values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "cm" : "in"}
                                      </InputGroup.Text>
                                    </InputGroup.Append>
                                  </InputGroup>
                                </Col>
                                <Col md="3">
                                  <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Prepend>
                                      <InputGroup.Text>W</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                        name={`packages.${index}.width`}
                                        type="number"
                                        onChange={(e) => {
                                          const widthValue = e.target.value ? parseFloat(e.target.value) : 0;
                                          if (aPackage.length && aPackage.height && aPackage.numberOfPackages) {
                                            const perUnitVolume = widthValue * aPackage.length * aPackage.height / 
                                              (values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? 1000000 : 1728);
                                            const totalVolume = perUnitVolume * aPackage.numberOfPackages;
                                            setFieldValue(`packages.${index}.volume`, Math.round(perUnitVolume * 1000) / 1000);
                                            setFieldValue('totalVolume', updateTotalVolume(totalVolume, index, values));
                                          }
                                          handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        value={aPackage.width}
                                      />
                                    <InputGroup.Append>
                                      <InputGroup.Text>
                                        {values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "cm" : "in"}
                                      </InputGroup.Text>
                                    </InputGroup.Append>
                                  </InputGroup>
                                </Col>
                                <Col md="3">
                                  <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Prepend>
                                      <InputGroup.Text>H</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                      name={`packages.${index}.height`}
                                      type="number"
                                      onChange={(e) => {
                                        const heightValue = e.target.value ? parseFloat(e.target.value) : 0;
                                        if (aPackage.width && aPackage.length && aPackage.numberOfPackages) {
                                          const perUnitVolume = heightValue * aPackage.width * aPackage.length / 
                                            (values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? 1000000 : 1728);
                                          const totalVolume = perUnitVolume * aPackage.numberOfPackages;
                                          setFieldValue(`packages.${index}.volume`, Math.round(perUnitVolume * 1000) / 1000);
                                          setFieldValue('totalVolume', updateTotalVolume(totalVolume, index, values));
                                        }
                                        handleChange(e);
                                      }}
                                      onBlur={handleBlur}
                                      value={aPackage.height}
                                    />
                                    <InputGroup.Append>
                                      <InputGroup.Text>
                                        {values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "cm" : "in"}
                                      </InputGroup.Text>
                                    </InputGroup.Append>
                                  </InputGroup>
                                </Col>
                                <Col md="3">
                                  <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Prepend>
                                      <InputGroup.Text>Weight</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                      name={`packages.${index}.weight`}
                                      type="number"
                                      onChange={(e) => {
                                        if (aPackage.numberOfPackages) {
                                          setFieldValue('totalWeight', updateTotalWeight(
                                            (e.target.value ? e.target.value : 0) * aPackage.numberOfPackages,
                                            index,
                                            values
                                          ));
                                        }
                                        handleChange(e);
                                      }}
                                      onBlur={handleBlur}
                                      value={aPackage.weight}
                                    />
                                    <InputGroup.Append>
                                      <InputGroup.Text>
                                        {values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "kg" : "lb"}
                                      </InputGroup.Text>
                                    </InputGroup.Append>
                                  </InputGroup>
                                </Col>
                              </Row>
                            )}

                            <Row>
                              <Col>
                                {values.packages.length === index + 1 && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => arrayHelpers.push({
                                      packageType: PACKAGE_TYPE.BOX,
                                      numberOfPackages: 1,
                                      packageMeasurementType: PACKAGE_MEASUREMENT_TYPE.DIMENSION,
                                      length: '',
                                      width: '',
                                      height: '',
                                      volume: '',
                                      weight: ''
                                    })}
                                    style={{ marginBottom: "10px" }}
                                  >
                                    {intl.formatMessage({ id: "ADD_PACKAGES_BUTTON" })}
                                  </Button>
                                )}
                                {values.packages.length !== index + 1 && <br />}
                              </Col>
                            </Row>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  />

                  <Row>
                    <Col md="5">
                      <Form.Group controlId="totalWeight">
                        <Form.Label>
                          {intl.formatMessage({ id: "TOTAL_WEIGHT" })}
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            name="totalWeight"
                            value={values.totalWeight}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={true}
                            className={touched.totalWeight && errors.totalWeight ? "is-invalid" : ""}
                          />
                          <InputGroup.Append>
                            <InputGroup.Text>
                              {values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "kg" : "lb"}
                            </InputGroup.Text>
                          </InputGroup.Append>
                        </InputGroup>
                        {touched.totalWeight && errors.totalWeight && (
                        <div className="fv-plugins-message-container">
                          <div className="fv-help-block">{errors.totalWeight}</div>
                        </div>
                      )}
                      </Form.Group>
                    </Col>

                    <Col md="5">
                      <Form.Group controlId="totalVolume">
                        <Form.Label>
                          {intl.formatMessage({ id: "TOTAL_VOLUME" })}
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            name="totalVolume"
                            value={values.totalVolume}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={true}
                            className={touched.totalVolume && errors.totalVolume ? "is-invalid" : ""}
                          />
                          <InputGroup.Append>
                            <InputGroup.Text>
                              {values.measurementSystem === MEASUREMENT_SYSTEM_TYPE.METRIC ? "cbm" : "cbf"}
                            </InputGroup.Text>
                          </InputGroup.Append>
                        </InputGroup>
                        {touched.totalVolume && errors.totalVolume && (
                        <div className="fv-plugins-message-container">
                          <div className="fv-help-block">{errors.totalVolume}</div>
                        </div>
                      )}
                      </Form.Group>
                    </Col>

                    <Col md="2">
                      <Form.Group controlId="isPickup">
                        <Form.Label>
                          {intl.formatMessage({ id: "IS_PICKUP_DESC" })}
                        </Form.Label>
                        <Form.Check
                          type="checkbox"
                          name="isPickup"
                          checked={values.isPickup}
                          onChange={(e) => setFieldValue('isPickup', e.target.checked)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />

                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="originDropOffOn">
                        <Form.Label>
                          {intl.formatMessage({ id: "ORIGIN_DROPOFF_ON" })}
                        </Form.Label>
                        <br />
                        <DatePicker
                          name="originDropOffOn"
                          selected={values.originDropOffOn}
                          onChange={(date) => setFieldValue('originDropOffOn', date)}
                          placeholderText={intl.formatMessage({ id: "SELECT_DATE" })}
                          className="form-control"
                          dateFormat="yyyy-MM-dd"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group controlId="note">
                        <Form.Label>{intl.formatMessage({ id: "CONSOL_NOTE" })}</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="note"
                          value={values.note}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={intl.formatMessage({ id: "CONSOL_NOTE" })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Modal.Body>

                <Modal.Footer>
                  <button
                    type="button"
                    onClick={() => onHide(false)}
                    className="btn btn-light btn-elevate"
                    disabled={isSubmitting}
                  >
                    {intl.formatMessage({ id: "CANCEL_BUTTON" })}
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting || isLoading}
                    className="btn btn-primary btn-elevate"
                  >
                    {intl.formatMessage({ id: isEditMode ? "UPDATE_BUTTON" : "SUBMIT_BUTTON" })}
                    {isSubmitting && <span className="ml-3 mr-5 spinner spinner-white"></span>}
                  </button>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>

      {/* Invite User Popup */}
      <InvitationDialog 
        show={showInviteUser}
        onHide={() => setShowInviteUser(false)}
        selectedBusinessEntity={selectedBusinessEntity} // Pass the object
        onInviteSuccess={handleInviteSuccess} // Handle success to reload/select
      />
    </>
  );
}